import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material'
import { Form, Formik} from 'formik'

import {
  useDELETE_adjustmentMutation,
  useGET_adjustmentByIdQuery,
  usePUT_adjustmentMutation,
  usePOST_adjustmentMutation,
} from 'redux/api/adjustmentsApi'

import { useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'

import { setCurriculumSelection, setGoalbankSelection } from 'redux/ui/goalbankUI'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SelectField from 'components/atoms/fields/SelectField'
import SectionTitle from 'components/atoms/SectionTitle'

import formModel, { initialValues } from 'utils/formModels/adjustmentModel'

import { 
  crudColorMap,
  crudIconMap,
  adjustmentTypeSelectData,
  translateGradeLevel,
  gradeSelectData,
  contentAreaSelectData,
  optionsYesNo
}  from 'utils/helpers'
import useCrudData from 'utils/customHooks/useCrudData'

import { ActionType } from 'types'

import { AppDispatch, RootState } from 'redux/store'
import { useDispatch, useSelector } from 'react-redux'

import _ from 'lodash'
import RichTextField from 'components/atoms/fields/RichTextField'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import { useNavigate } from 'react-router-dom'
import AccommodationList from 'components/utilized/common/AccommodationList'
import ModificationList from 'components/utilized/common/ModificationList'
import RadioField from 'components/atoms/fields/RadioField'
import DialogPopup from 'components/molecules/DialogPopup'
import GoalbankSelection from 'components/utilized/goalbank/GoalbankSelection'
import { FlagCircle } from '@mui/icons-material'
import GoalbankPage from 'pages/protected/GoalbankPage'
import InputField from 'components/atoms/fields/InputField'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const AdjustmentForm: FC<IProps> = ({  action, id, viewOnly }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  
  const backToSlug = `/adjustments`

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.accommodations-and-modifications')}`,
    entityId: id,
    getEntityQuery: useGET_adjustmentByIdQuery,
    createEntityMutation: usePOST_adjustmentMutation,
    editEntityMutation: usePUT_adjustmentMutation,
    deleteEntityMutation: useDELETE_adjustmentMutation,
  })

  const {
    student,
    grade_level,
    content_areas,
    other_content_area,
    adjustment_type,
    accommodations,
    modifications,
    start_date,
    end_date,
    notes,
  } = formModel()


  const selected_content_areas = entity?.content_areas.map(c => { return {id: c, title: t(`content-areas.${c}`)}}) ?? []
  if(entity?.other_content_area)
    selected_content_areas.push({id: 'other', title: t('content-areas.other')})

  const serverValues = entity?.id ? {
    ...entity,

    grade_level: entity.grade_level >= 0 ? {
      id: entity.grade_level,
      title: translateGradeLevel(entity.grade_level, true)
    } : null,

    content_areas: selected_content_areas,
    
    accommodations:  _.uniqBy(
      entity.accommodations.map(a => {return {category_id: a.category_id, category_title: a.category_title}}) as any[],
      a => [a.category_id, a.category_title].join()
    ).map(a => { return {
      category: {
        id: a.category_id,
        title: a.category_title
      },
      items: entity.accommodations.filter(item => a.category_id === item.category_id)
    }}),

    modifications:  _.uniqBy(
      entity.modifications.map(m=> {return {category_id: m.category_id, category_title: m.category_title}}) as any[],
      m => [m.category_id, m.category_title].join()
    ).map(m => { return {
      category: {
        id: m.category_id,
        title: m.category_title
      },
      items: entity.modifications.filter(item => m.category_id === item.category_id)
    }}),

    adjustment_type: entity.adjustment_type ? {
      id: entity.adjustment_type,
      title: t('common.'+entity.adjustment_type)
    } : null,

    

  } : {}


  const formValues = {
    ...initialValues,
    ...serverValues,
  }

  const [formVisible, setFormVisible] = useState(false)
  const [activeQuestion, setActiveQuestion] = useState(0)

  const questions = [
    {
      "text": t('adjustments.question-1'), 
      "note": "",
      "value": null
    },
    {
      "text": t('adjustments.question-2'), 
      "note": "",
      "value": null
    }, 
    {
      "text": t('adjustments.question-3'), 
      "note": t('adjustments.note-3'),
      "value": null
    },
  ]
 
  const options = [
    {
      label: t('common.yes'),
      value: 1,
    },
    {
      label: t('common.no'),
      value: 0,
    }
  ]


  const [goalbankOpen, setGoalbankOpen] = useState(false)
  const goalbankSelection = useSelector((state: RootState) => state['goalbank'].goalbankSelection)
  const curriculumSelection = useSelector((state: RootState) => state['goalbank'].curriculumSelection)

  useEffect(() => {
    dispatch(setGoalbankSelection(entity?.goalbank_selection ?? {}))
    dispatch(setCurriculumSelection(entity?.curriculum_selection ?? {}))
  }, [entity])

  return ( 
        <Formik
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={true}
          initialValues={formValues}
          onSubmit={(values) => {

            const data = {
              ...values,
              content_areas: values.content_areas.filter(c => c.id !== 'other').map(c => c.id),
              grade_level: values.grade_level?.id ?? '0',
              accommodations_ids: values?.adjustment_type?.id == 'accommodation' 
                ? values?.accommodations.map(a => a.items.map(item => item.id)).flat() 
                : [],
              modifications_ids: values?.adjustment_type?.id == 'modification' 
                ? values?.modifications.map(m => m.items.map(item => item.id)).flat()
                : [],
              student_id: values?.student?.id ?? '',
              adjustment_type: values?.adjustment_type?.id ?? '',
              goalbank_selection: goalbankSelection,
              curriculum_selection: curriculumSelection
            }

            handleSubmit(action, id, data)
          }}
        >
          {({ values, setFieldValue, errors }) => {
            return (
              <Form>

                  <Grid container spacing={4} mt={1} >

                    <Grid item xs={12} md={3}>
                      <SelectField
                        fieldProps={{
                          ...student,
                        }}
                        apiCall={useGET_studentsProfileSelectQuery}
                        disabled={viewOnly}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <SelectField
                        fieldProps={{ ...grade_level }}
                        apiCall={gradeSelectData}
                        disabled={viewOnly}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <SelectField
                        fieldProps={{
                          ...content_areas,
                        }}
                        apiCall={contentAreaSelectData}
                        multiple={true}
                      />

                      {values.content_areas.map(c => c.id).indexOf('other') > -1 && 
                        <InputField fieldProps={{ ...other_content_area }} disabled={viewOnly} sx={{ marginTop: '30px' }}/>}
                    </Grid>

                    <Grid item xs={12} md={3} >
                          <Button
                              onClick={() => setGoalbankOpen(true)}
                              aria-label={`${t('goalbank.use-curriculum-library')}`}
                              size='small'
                              disabled={viewOnly}
                              color='success'
                              variant='contained'
                              sx={{ boxShadow: 'none',  minHeight: '47px' }}
                            >
                              <FlagCircle style={{ fontSize: '1.5rem', marginRight: '10px' }} /> {t('goalbank.use-curriculum-library')}
                          </Button>
                    </Grid>

                    <Grid item xs={12}>      
                      <GoalbankSelection goalbankSelection={goalbankSelection} curriculumSelection={curriculumSelection} />
                    </Grid>      
                  </Grid>   


                  {values.student && values.grade_level && values.content_areas.length > 0 && !id && <>
                      <Typography sx={{ fontSize: '20px', marginBottom: '10px', marginTop: '40px' }}>{t(`adjustments.intro`)}</Typography>
                    
                       {questions.map((q,k) => 
                        activeQuestion >= k && <Grid container spacing={1} mt={3} key={`question-${k}`}>
                          <Grid item xs={12}>
                            <Typography sx={{ fontSize: '20px', marginBottom: '10px', fontWeight: 'bold' }}>{t(`common.question`)} {k+1}</Typography>
                            <Typography>{`${q.text}`}</Typography>

                            {q.note && <Typography sx={{ 
                              fontSize: '0.7rem',
                              fontStyle: 'italic'
                            }}>{`*${q.note}`}</Typography>}
                          </Grid>
                          <Grid item xs={12} sx={{ marginBottom: '0.5rem', display: 'flex' }}>
                            <RadioField
                              fieldProps={{
                                label: '',
                                name: `q${k}`,
                              }}
                              options={optionsYesNo}
                              onChange={(newValue) => {

                                newValue = newValue == 1
                                
                                if(k==0 && newValue){
                                  setFormVisible(true)
                                  setFieldValue('adjustment_type', { id: 'modification', title: t('common.modification') })
                                  setActiveQuestion(0)
                                  return
                                }
                                  
                                if(k==1 && newValue){
                                  setFormVisible(true)
                                  setFieldValue('adjustment_type', { id: 'modification', title: t('common.modification') })
                                  setActiveQuestion(1)
                                  return
                                }
                                
                                if(k==2 && newValue){
                                  setFormVisible(true)
                                  setFieldValue('adjustment_type', { id: 'accommodation', title: t('common.accommodation') })
                                  setActiveQuestion(2)
                                  return
                                }
                                
                                if(k==2 && !newValue){
                                  setFormVisible(true)
                                  setFieldValue('adjustment_type', { id: 'modification', title: t('common.modification') })
                                  setActiveQuestion(2)
                                  return
                                }
                                
                                setActiveQuestion(activeQuestion + 1)
                              }}
                            />
                          </Grid>
                        </Grid>
                    )}
                  </> 
                }

                
                {(id || formVisible) &&
                <>
                <div style={{ height: '50px' }}></div>
                <Grid container spacing={4} sx={{ marginTop: '-1rem' }}  >

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                   <SelectField
                      fieldProps={{
                        ...adjustment_type,
                      }}
                      apiCall={adjustmentTypeSelectData}
                      disabled={true}
                    />
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <DatePickerField fieldProps={{ ...start_date  }} disabled={viewOnly} />
                  </Grid>     

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <DatePickerField fieldProps={{ ...end_date  }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important' }}>
                    <RichTextField
                      fieldProps={{ ...notes }}
                      label={notes.label}
                      disabled={viewOnly}
                     />
                  </Grid>

                  {values.adjustment_type?.id == 'accommodation' &&
                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important', paddingBottom: '20px !important' }}> 
                    <AccommodationList fieldProps={{ ...accommodations }}/>
                  </Grid>       
                  }

                  {values.adjustment_type?.id == 'modification' &&  
                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important', paddingBottom: '20px !important' }}> 
                    <ModificationList fieldProps={{ ...modifications }}/>
                  </Grid> 
                  }      

                </Grid>
                

                {/* ACTIONS */}
                <Box sx={{ marginTop: '20px' }}>

                  <Box sx={{ display: 'inline-block' }}>   
                    <Button
                      variant='outlined'
                      color='black'
                      onClick={() => navigate(backToSlug)}
                      sx={{ minHeight: '2.5rem', width: '10rem' }}
                    >
                      <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                      {`${t('common.back')}`}
                    </Button>
                  </Box> 

                  <Box sx={{ display: 'inline-block', marginLeft: '10px' }}>   
                    <SubmitButton
                              color={crudColorMap[action]}
                              text={`${t('form-actions.' + action)}`}
                              icon={crudIconMap[action]}
                              sx={{ minHeight: '2.5rem', width: '10rem' }}
                              isLoading={isLoading}
                          />
                  </Box>   
                </Box>
                </>
                }

                <DialogPopup
                  title={t('common.goalbank')}
                  open={goalbankOpen}
                  onClose={() => setGoalbankOpen(false)}
                  popupWidth={'1400px'}
                >
                  <GoalbankPage 
                    hasSelection={true} 
                    visibleSections={['skills', 'measures', 'student_learning_objectives']}
                    filters={{
                        grade_level: values.grade_level?.id,
                    }}
                  />
                </DialogPopup>

              </Form>
            )
          }}
        </Formik>
  )
}
export default AdjustmentForm
